import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Folder, MessageSquare, FileText, ChevronRight } from "lucide-react";
import { getForumDiscussions } from "../../services/moodle/forum";

export const CategoryCard = ({ category }) => {
  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isDark = theme.mode === "dark";

  const handleMouseEnter = (e) => {
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)";
    e.currentTarget.style.borderColor = "#ECB236";

    if (auth.user?.token && category?.id) {
      queryClient.prefetchQuery({
        queryKey: ["forum", "discussions", String(category.id)],
        queryFn: async () => {
          const data = await getForumDiscussions({
            token: auth.user.token,
            categoryId: category.id,
          });
          return Array.isArray(data) ? data : [];
        },
        staleTime: 60_000,
      });
    }
  };

  return (
    <>
      <style>{`
        .forum-category-card {
          background-color: ${isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff")};
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 14px;
          padding: 18px 22px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 20px;
          position: relative;
          overflow: hidden;
          font-family: 'Montserrat', sans-serif;
        }
        @media (max-width: 767px) {
          .forum-category-card {
            display: grid !important;
            grid-template-columns: 42px 1fr !important;
            gap: 8px 12px !important;
            padding: 14px 16px !important;
            align-items: center !important;
          }
          .forum-category-card .cat-icon {
            width: 42px !important;
            height: 42px !important;
            grid-column: 1 !important;
            grid-row: 1 !important;
          }
          .forum-category-card .cat-icon svg {
            width: 22px !important;
            height: 22px !important;
          }
          .forum-category-card .cat-content {
            grid-column: 2 !important;
            grid-row: 1 !important;
            gap: 2px !important;
          }
          .forum-category-card .cat-title {
            font-size: 17px !important;
            white-space: normal !important;
            word-break: break-word !important;
          }
          .forum-category-card .cat-desc {
            font-size: 15px !important;
            white-space: normal !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 1 !important;
            -webkit-box-orient: vertical !important;
          }
          .forum-category-card .cat-stats {
            grid-column: 1 / -1 !important;
            grid-row: 2 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            padding-top: 10px !important;
            margin-top: 4px !important;
            border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
            gap: 12px !important;
          }
          .forum-category-card .cat-stat-num {
            font-size: 16px !important;
          }
          .forum-category-card .cat-stat-label {
            font-size: 15px !important;
          }
        }
      `}</style>
      <div
        onClick={() => navigate(`/foro/${category.id}`)}
        className="forum-category-card"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.12)";
        }}
      >
        {/* Folder Icon */}
        <div
          className="cat-icon"
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            backgroundColor: "rgba(236, 178, 54, 0.1)",
            border: "1px solid rgba(236, 178, 54, 0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ECB236",
            flexShrink: 0,
          }}
        >
          <Folder size={26} />
        </div>

        {/* Content */}
        <div className="cat-content" style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
          <h3
            className="cat-title"
            style={{
              color: "#ffffff",
              margin: 0,
              fontSize: "20px",
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {category.name}
          </h3>
          <p
            className="cat-desc"
            style={{
              margin: 0,
              color: "#94a3b8",
              fontSize: "15px",
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {category.description || "Conversaciones y dudas de la comunidad."}
          </p>
        </div>

        {/* Stats on the right */}
        <div className="cat-stats" style={{ display: "flex", alignItems: "center", gap: "24px", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MessageSquare size={18} style={{ color: "#94a3b8" }} />
            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
              <span className="cat-stat-num" style={{ color: "#ffffff", fontSize: "18px", fontWeight: 700, lineHeight: 1 }}>
                {category.discussion_count || 0}
              </span>
              <span className="cat-stat-label" style={{ color: "#94a3b8", fontSize: "15px", fontWeight: 500 }}>
                {category.discussion_count === 1 ? "Tema" : "Temas"}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FileText size={18} style={{ color: "#94a3b8" }} />
            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
              <span className="cat-stat-num" style={{ color: "#ffffff", fontSize: "18px", fontWeight: 700, lineHeight: 1 }}>
                {category.post_count || 0}
              </span>
              <span className="cat-stat-label" style={{ color: "#94a3b8", fontSize: "15px", fontWeight: 500 }}>
                {category.post_count === 1 ? "Mensaje" : "Mensajes"}
              </span>
            </div>
          </div>

          <ChevronRight size={20} style={{ color: "#64748b" }} />
        </div>
      </div>
    </>
  );
};

export default CategoryCard;
